import React, { useState, useEffect, useRef } from 'react'
import { useApp } from '../../context/AppContext'
import { spin, getCategorieAfectata, getMesajPierdere, calcImpactCheltuieli } from '../../lib/slotLogic'
import { supabase } from '../../lib/supabase'
import './Slot.css'

const SIMBOLURI = ['cherries', 'lemon', 'orange', 'grapes', 'watermelon', 'seven', 'plum']

// Mizele disponibile — userul poate juca pe oricare
const MIZE = [2, 4, 8, 10, 20, 50, 100]

const EXPENSE_ICONS = {
  mancare_zilnic: '🍞',
  utilitati_zilnic: '💡',
  rata_zilnic: '🏠',
  copii_zilnic: '👶',
  transport_zilnic: '🚌',
  alte_zilnic: '📦',
}

// Audio catalog — files in public/audio/
const AUDIO_CATALOG = {
  spin: Array.from({ length: 9 }, (_, i) => `/audio/spin/spin${i + 1}.mp3`),
  mancare: Array.from({ length: 7 }, (_, i) => `/audio/mancare/masa${i + 1}.mp3`),
  utilitati: Array.from({ length: 5 }, (_, i) => `/audio/utilitati/utilitati${i + 1}.mp3`),
  rata: Array.from({ length: 6 }, (_, i) => `/audio/rata/rata${i + 1}.mp3`),
  copii: Array.from({ length: 7 }, (_, i) => `/audio/copii/copil${i + 1}.mp3`),
  datorie: Array.from({ length: 7 }, (_, i) => `/audio/datorie/datorie${i + 1}.mp3`),
  castig: Array.from({ length: 6 }, (_, i) => `/audio/castig/castig${i + 1}.mp3`),
}

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)]
}

export default function Slot() {
  const { user, profile } = useApp()
  const slotConfig = profile?.slot_config
  const cheltuieli = profile?.profil_financiar?.cheltuieli
  const mesaje = profile?.mesaje_personalizate
  const context = profile?.context
  const datoriiConfig = profile?.datorii_config

  const bugetZilnic = slotConfig?.buget_sesiune_azi || 30

  const [buget, setBuget] = useState(null) // null = loading
  const [bugetLoaded, setBugetLoaded] = useState(false)
  const [spinning, setSpinning] = useState(false)
  const [reels, setReels] = useState([
    ['plum', 'seven', 'cherries', 'seven', 'plum'],
    ['lemon', 'grapes', 'watermelon', 'grapes', 'lemon'],
    ['orange', 'plum', 'seven', 'plum', 'orange'],
  ])
  const [stoppedCols, setStoppedCols] = useState([true, true, true, true, true])
  const stoppedRef = useRef([true, true, true, true, true])
  const [mesaj, setMesaj] = useState(null) // { text, tip: 'win'|'lose'|'datorie' }
  const [totalPierdut, setTotalPierdut] = useState(0)
  const [totalCastigat, setTotalCastigat] = useState(0)
  const [spinuri, setSpinuri] = useState(0)
  const [inDatarie, setInDatarie] = useState(false)
  const [datorieTotal, setDatorieTotal] = useState(0)
  const [prieten, setPrieten] = useState(null)
  const [sesiuneFinalizata, setSesiuneFinalizata] = useState(false)
  const [datoriiLista, setDatoriiLista] = useState([]) // [{nume, suma, timestamp}]
  const [winPayline, setWinPayline] = useState(null)
  const [winMatchCount, setWinMatchCount] = useState(0)
  const [mizaCurenta, setMizaCurenta] = useState(2)
  const [baniDeAcasa, setBaniDeAcasa] = useState(0) // total bagat de acasa
  const [showBaniAcasa, setShowBaniAcasa] = useState(false)
  const [sumaAcasa, setSumaAcasa] = useState('')
  const [autoplay, setAutoplay] = useState(false)
  const [audioPlaying, setAudioPlaying] = useState(false)
  const autoplayRef = useRef(false)
  const bugetRef = useRef(null)
  const totalPierdutRef = useRef(0)
  const totalCastigatRef = useRef(0)
  const spinuriRef = useRef(0)
  const inDatorieRef = useRef(false)
  const datorieTotalRef = useRef(0)
  const datoriiListaRef = useRef([])
  const baniDeAcasaRef = useRef(0)
  const audioRef = useRef(null)
  const intervalRef = useRef(null)
  const autoplayTimer = useRef(null)

  // Keep refs in sync
  useEffect(() => { bugetRef.current = buget }, [buget])
  useEffect(() => { totalPierdutRef.current = totalPierdut }, [totalPierdut])
  useEffect(() => { totalCastigatRef.current = totalCastigat }, [totalCastigat])
  useEffect(() => { spinuriRef.current = spinuri }, [spinuri])
  useEffect(() => { inDatorieRef.current = inDatarie }, [inDatarie])
  useEffect(() => { datorieTotalRef.current = datorieTotal }, [datorieTotal])
  useEffect(() => { datoriiListaRef.current = datoriiLista }, [datoriiLista])
  useEffect(() => { baniDeAcasaRef.current = baniDeAcasa }, [baniDeAcasa])

  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current)
      clearTimeout(autoplayTimer.current)
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null }
      // Save on unmount (navigating away)
      const snap = {
        buget: bugetRef.current ?? 0,
        spinuri: spinuriRef.current,
        pierderi: totalPierdutRef.current,
        castiguri: totalCastigatRef.current,
        inDatarie: inDatorieRef.current,
        datorieTotal: datorieTotalRef.current,
        datoriiLista: datoriiListaRef.current,
        baniDeAcasa: baniDeAcasaRef.current,
      }
      const today = new Date().toISOString().split('T')[0]
      // Fire-and-forget save
      supabase.from('user_profiles')
        .update({ buget_total: Math.max(0, snap.buget) })
        .eq('user_id', user.id).then(() => {})
      supabase.from('progres_zilnic').upsert({
        user_id: user.id,
        data: today,
        slot_sesiune: {
          spinuri_folosite: snap.spinuri,
          buget_ramas: Math.max(0, snap.buget),
          pierderi_totale: snap.pierderi,
          castiguri_totale: snap.castiguri,
          in_datorie: snap.inDatarie,
          datorie_curenta: snap.datorieTotal,
          datorii: snap.datoriiLista,
          bani_de_acasa: snap.baniDeAcasa,
        }
      }, { onConflict: 'user_id,data' }).then(() => {})
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Incarca bugetul persistent si sesiunea de azi
  useEffect(() => {
    async function incarca() {
      // 1. Fetch buget_total + buget_ultima_zi from user_profiles
      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('buget_total, buget_ultima_zi')
        .eq('user_id', user.id)
        .single()

      let bugetTotal = profileData?.buget_total
      const ultimaZi = profileData?.buget_ultima_zi
      const today = new Date().toISOString().split('T')[0]

      // 2. Check if today's session exists
      const { data: sesiuneData } = await supabase
        .from('progres_zilnic')
        .select('slot_sesiune')
        .eq('user_id', user.id)
        .eq('data', today)
        .single()

      const s = sesiuneData?.slot_sesiune

      if (s) {
        // Session exists — restore state
        setSpinuri(s.spinuri_folosite || 0)
        setTotalPierdut(s.pierderi_totale || 0)
        setTotalCastigat(s.castiguri_totale || 0)
        setInDatarie(!!s.in_datorie)
        setDatorieTotal(s.datorie_curenta || 0)
        setDatoriiLista(s.datorii || [])
        setBaniDeAcasa(s.bani_de_acasa || 0)
        const bt = bugetTotal ?? bugetZilnic
        setBuget(bt)
        // Daca bugetul e 0, arata optiunile de bani
        if (bt <= 0) {
          setTimeout(() => triggerOptiuniBani(), 100)
        }
      } else if (ultimaZi !== today) {
        // New day — add daily allowance
        if (bugetTotal == null) {
          bugetTotal = bugetZilnic
        } else {
          bugetTotal = parseFloat((bugetTotal + bugetZilnic).toFixed(2))
        }
        setBuget(bugetTotal)
        await supabase
          .from('user_profiles')
          .update({ buget_total: bugetTotal, buget_ultima_zi: today })
          .eq('user_id', user.id)
      } else {
        // Same day, no session yet — just use existing total
        const bt = bugetTotal ?? bugetZilnic
        setBuget(bt)
        if (bt <= 0) {
          setTimeout(() => triggerOptiuniBani(), 100)
        }
      }

      setBugetLoaded(true)
    }
    if (user?.id) incarca()
  }, [user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Salveaza sesiunea la final sau la unmount
  useEffect(() => {
    if (sesiuneFinalizata) salveazaSesiunea()
  }, [sesiuneFinalizata]) // eslint-disable-line react-hooks/exhaustive-deps

  async function salveazaSesiunea() {
    const today = new Date().toISOString().split('T')[0]
    const snap = buildSnapshot()
    await supabase.from('progres_zilnic').upsert({
      user_id: user.id,
      data: today,
      slot_sesiune: {
        spinuri_folosite: snap.spinuri,
        buget_ramas: Math.max(0, snap.buget),
        pierderi_totale: snap.pierderi,
        castiguri_totale: snap.castiguri,
        in_datorie: snap.inDatarie,
        datorie_curenta: snap.datorieTotal,
        datorii: snap.datoriiLista,
        bani_de_acasa: snap.baniDeAcasa,
      }
    }, { onConflict: 'user_id,data' })
    await supabase
      .from('user_profiles')
      .update({ buget_total: Math.max(0, snap.buget) })
      .eq('user_id', user.id)
  }

  // Save after every spin (buget + session)
  async function salveazaDupaSpin(snapshot) {
    const today = new Date().toISOString().split('T')[0]
    await Promise.all([
      supabase.from('user_profiles')
        .update({ buget_total: Math.max(0, snapshot.buget) })
        .eq('user_id', user.id),
      supabase.from('progres_zilnic').upsert({
        user_id: user.id,
        data: today,
        slot_sesiune: {
          spinuri_folosite: snapshot.spinuri,
          buget_ramas: Math.max(0, snapshot.buget),
          pierderi_totale: snapshot.pierderi,
          castiguri_totale: snapshot.castiguri,
          in_datorie: snapshot.inDatarie,
          datorie_curenta: snapshot.datorieTotal,
          datorii: snapshot.datoriiLista,
          bani_de_acasa: snapshot.baniDeAcasa,
        }
      }, { onConflict: 'user_id,data' })
    ])
  }

  function stopAutoplay() {
    autoplayRef.current = false
    setAutoplay(false)
    clearTimeout(autoplayTimer.current)
  }

  function toggleAutoplay() {
    if (autoplay) {
      stopAutoplay()
    } else {
      autoplayRef.current = true
      setAutoplay(true)
      if (!spinning) doSpin()
    }
  }

  // Pick audio category based on spin result and user context
  function pickAudioCategory(isWin) {
    if (isWin) return 'castig'

    const categoriiDisponibile = ['spin', 'mancare', 'utilitati']
    if (context?.are_rata) categoriiDisponibile.push('rata')
    if (context?.are_copii) categoriiDisponibile.push('copii')
    if (inDatarie || datoriiLista.length > 0) categoriiDisponibile.push('datorie')

    return pickRandom(categoriiDisponibile)
  }

  function playAudio(isWin) {
    return new Promise((resolve) => {
      const cat = pickAudioCategory(isWin)
      const files = AUDIO_CATALOG[cat]
      if (!files || files.length === 0) { resolve(); return }

      const src = pickRandom(files)
      const audio = new Audio(src)
      audioRef.current = audio
      setAudioPlaying(true)

      audio.onended = () => { setAudioPlaying(false); audioRef.current = null; resolve() }
      audio.onerror = () => { setAudioPlaying(false); audioRef.current = null; resolve() }
      audio.play().catch(() => { setAudioPlaying(false); audioRef.current = null; resolve() })
    })
  }

  function doSpin() {
    if (spinning || sesiuneFinalizata || !bugetLoaded || audioPlaying) return
    if (bugetActual <= 0 && !inDatarie) {
      stopAutoplay()
      intraDatorie()
      return
    }

    setSpinning(true)
    setMesaj(null)
    setWinPayline(null)
    setWinMatchCount(0)
    setStoppedCols([false, false, false, false, false])
    stoppedRef.current = [false, false, false, false, false]

    // Pre-calculate the final result
    const rezultat = spin(slotConfig, mizaCurenta)
    const finalGrid = rezultat.simboluri

    // Animate: randomize only non-stopped columns
    intervalRef.current = setInterval(() => {
      setReels(prev => {
        const next = prev.map(row => [...row])
        for (let col = 0; col < 5; col++) {
          if (!stoppedRef.current[col]) {
            for (let row = 0; row < 3; row++) {
              next[row][col] = SIMBOLURI[Math.floor(Math.random() * SIMBOLURI.length)]
            }
          }
        }
        return next
      })
    }, 60)

    // Stop columns one by one, left to right
    const stopDelays = [600, 900, 1200, 1500, 1800]
    stopDelays.forEach((delay, colIdx) => {
      setTimeout(() => {
        // Mark column as stopped in ref (for interval) and state (for CSS)
        stoppedRef.current[colIdx] = true
        setStoppedCols(prev => {
          const next = [...prev]
          next[colIdx] = true
          return next
        })
        // Place final symbols for this column
        setReels(prev => {
          const next = prev.map(row => [...row])
          for (let row = 0; row < 3; row++) {
            next[row][colIdx] = finalGrid[row][colIdx]
          }
          return next
        })

        // Last column stopped — finalize
        if (colIdx === 4) {
          clearInterval(intervalRef.current)
          setReels(finalGrid)
          finalizeazaSpin(rezultat)
        }
      }, delay)
    })
  }

  function buildSnapshot(overrides = {}) {
    return {
      buget: overrides.buget ?? bugetRef.current ?? 0,
      spinuri: overrides.spinuri ?? spinuriRef.current,
      pierderi: overrides.pierderi ?? totalPierdutRef.current,
      castiguri: overrides.castiguri ?? totalCastigatRef.current,
      inDatarie: overrides.inDatarie ?? inDatorieRef.current,
      datorieTotal: overrides.datorieTotal ?? datorieTotalRef.current,
      datoriiLista: overrides.datoriiLista ?? datoriiListaRef.current,
      baniDeAcasa: overrides.baniDeAcasa ?? baniDeAcasaRef.current,
    }
  }

  function finalizeazaSpin(rezultat) {
    setSpinning(false)
    setSpinuri(prev => prev + 1)
    if (rezultat.winPayline != null) {
      setWinPayline(rezultat.winPayline)
      setWinMatchCount(rezultat.matchCount || 0)
    }

    const bugetCurent = bugetRef.current || 0
    const nouSpinuri = spinuriRef.current + 1

    if (rezultat.type === 'win') {
      const castig = rezultat.suma
      const nouBuget = parseFloat((bugetCurent + castig).toFixed(2))
      const nouCastiguri = parseFloat((totalCastigatRef.current + castig).toFixed(2))
      setBuget(nouBuget)
      setTotalCastigat(nouCastiguri)
      setMesaj({ text: mesaje?.castig_avertisment || 'Ai castigat! Dar mai putin decat ai bagat.', tip: 'win' })
      salveazaDupaSpin(buildSnapshot({ buget: nouBuget, spinuri: nouSpinuri, castiguri: nouCastiguri }))
      playAudio(true).then(() => scheduleAutoplay())
    } else {
      const miza = mizaCurenta
      const nouPierderi = parseFloat((totalPierdutRef.current + miza).toFixed(2))
      setTotalPierdut(nouPierderi)

      if (inDatorieRef.current) {
        const nouDatorieTotal = parseFloat((datorieTotalRef.current + miza).toFixed(2))
        setDatorieTotal(nouDatorieTotal)
        salveazaDupaSpin(buildSnapshot({ buget: 0, spinuri: nouSpinuri, pierderi: nouPierderi, inDatarie: true, datorieTotal: nouDatorieTotal }))
      } else {
        const nouBuget = parseFloat((bugetCurent - miza).toFixed(2))
        setBuget(nouBuget)
        salveazaDupaSpin(buildSnapshot({ buget: Math.max(0, nouBuget), spinuri: nouSpinuri, pierderi: nouPierderi }))
        if (nouBuget <= 0) {
          intraDatorie()
          return
        }
      }

      const categorie = getCategorieAfectata(cheltuieli || {}, context || {})
      const msgText = getMesajPierdere(categorie, mesaje || {}, miza)
      setMesaj({ text: msgText, tip: 'lose' })
      playAudio(false).then(() => scheduleAutoplay())
    }
  }

  function scheduleAutoplay() {
    if (!autoplayRef.current) return
    autoplayTimer.current = setTimeout(() => {
      if (autoplayRef.current) doSpin()
    }, 800)
  }

  function triggerOptiuniBani() {
    setInDatarie(true)
    const p = datoriiConfig?.prieteni?.[Math.floor(Math.random() * (datoriiConfig?.prieteni?.length || 1))]
    setPrieten(p)
    setShowBaniAcasa(true)
    setMesaj({ text: 'Nu ai buget. Aduci bani de acasa sau imprumuti?', tip: 'datorie' })
  }

  function intraDatorie() {
    stopAutoplay()
    triggerOptiuniBani()
  }

  function bagaBaniDeAcasa() {
    const venitNet = profile?.profil_financiar?.venit_lunar || 3000
    const suma = parseFloat(sumaAcasa)
    if (!suma || suma <= 0) return
    const totalDupaBagat = baniDeAcasa + suma
    if (totalDupaBagat > venitNet) {
      const ramas = Math.max(0, venitNet - baniDeAcasa)
      if (ramas <= 0) {
        setMesaj({ text: `Ai cheltuit tot venitul tau lunar (${venitNet} lei). Nu mai ai de unde lua bani de acasa.`, tip: 'datorie' })
        return
      }
      setMesaj({ text: `Mai poti baga maxim ${ramas.toFixed(0)} lei din venitul tau.`, tip: 'datorie' })
      return
    }
    setBaniDeAcasa(totalDupaBagat)
    setBuget(suma)
    setInDatarie(false)
    setShowBaniAcasa(false)
    setSumaAcasa('')
    setPrieten(null)
    setMesaj({ text: `Ai bagat ${suma} lei din banii tai de acasa. Bani pe care familia ta ii simte lipsa.`, tip: 'datorie' })
    // Save to DB immediately
    salveazaDupaSpin(buildSnapshot({ buget: suma, inDatarie: false, baniDeAcasa: totalDupaBagat }))
  }

  function imprumutaDeLaPrieten() {
    if (!prieten) return
    const nouaDatorie = {
      nume: prieten.nume,
      suma: prieten.suma_disponibila,
      timestamp: new Date().toISOString(),
    }
    const nouListaDatorii = [...datoriiLista, nouaDatorie]
    const nouDatorieTotal = parseFloat((datorieTotal + prieten.suma_disponibila).toFixed(2))
    setDatoriiLista(nouListaDatorii)
    setDatorieTotal(nouDatorieTotal)
    setBuget(prieten.suma_disponibila)
    setInDatarie(false)
    setShowBaniAcasa(false)
    setPrieten(null)
    setMesaj({ text: `${prieten.nume} ti-a dat ${prieten.suma_disponibila} lei. Dar trebuie sa ii dai inapoi.`, tip: 'datorie' })
    // Save to DB immediately
    salveazaDupaSpin(buildSnapshot({ buget: prieten.suma_disponibila, inDatarie: false, datorieTotal: nouDatorieTotal, datoriiLista: nouListaDatorii }))
  }

  function opreste() {
    setSesiuneFinalizata(true)
  }

  const impactCheltuieli = calcImpactCheltuieli(totalPierdut, cheltuieli || {})
  const bugetActual = buget || 0
  const spinuriRamase = Math.max(0, Math.floor(bugetActual / mizaCurenta))

  const PAYLINE_ROWS = [
    [0, 0, 0, 0, 0], // top
    [1, 1, 1, 1, 1], // middle
    [2, 2, 2, 2, 2], // bottom
    [0, 1, 2, 1, 0], // V
    [2, 1, 0, 1, 2], // Λ
  ]

  function isOnWinPayline(rowIdx, colIdx) {
    if (winPayline == null) return false
    return colIdx < winMatchCount && PAYLINE_ROWS[winPayline][colIdx] === rowIdx
  }

  if (!bugetLoaded) {
    return (
      <div className="slot-page">
        <p>Se incarca simulatorul...</p>
      </div>
    )
  }

  if (sesiuneFinalizata) {
    return (
      <div className="slot-page">
        <div className="slot-final">
          <h2>Sesiunea de azi s-a incheiat</h2>
          <div className="final-stats">
            <div className="final-stat">
              <div className="final-stat-num red">{totalPierdut} lei</div>
              <div className="final-stat-lbl">pierdut total</div>
            </div>
            <div className="final-stat">
              <div className="final-stat-num green">{totalCastigat} lei</div>
              <div className="final-stat-lbl">castigat total</div>
            </div>
            <div className="final-stat">
              <div className="final-stat-num">{spinuri}</div>
              <div className="final-stat-lbl">spinuri totale</div>
            </div>
          </div>
          <div className="final-impact">
            <h3>Impactul asupra bugetului tau</h3>
            {Object.entries(impactCheltuieli).filter(([, v]) => v.pierdut > 0).map(([key, val]) => (
              <div key={key} className="impact-row">
                <span>{labelCheltuiala(key)}</span>
                <span className="impact-val red">-{val.pierdut} lei ({val.procent}%)</span>
              </div>
            ))}
          </div>
          {datoriiLista.length > 0 && (
            <div className="final-datorie">
              <strong>Datorii acumulate: {datorieTotal} lei</strong>
              <ul className="datorii-lista">
                {datoriiLista.map((d, i) => (
                  <li key={i}>{d.nume}: {d.suma} lei</li>
                ))}
              </ul>
            </div>
          )}
          {baniDeAcasa > 0 && (
            <div className="final-datorie">
              <strong>💸 Bani bagati de acasa: {baniDeAcasa} lei</strong>
              <p style={{ margin: '6px 0 0', fontSize: '13px' }}>Bani luati din buzunarul familiei tale.</p>
            </div>
          )}
          <p className="final-msg">Casa castiga mereu. Asta nu e un joc — e o taxa pe speranta.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="slot-page">
      <div className="slot-header">
        <h1>Simulatorul tau</h1>
        <div className="slot-buget-display">
          <span className={`buget-num ${inDatarie ? 'red' : ''}`}>
            {inDatarie ? `-${datorieTotal} lei` : `${bugetActual.toFixed(2)} lei`}
          </span>
          <span className="buget-lbl">{inDatarie ? 'in datorie' : 'buget ramas'}</span>
        </div>
      </div>

      <div className="slot-body">
        {/* MACHINE */}
        <div className="slot-machine">
          <div className="slot-grid">
            {reels.map((row, rowIdx) => (
              <div key={rowIdx} className="slot-row">
                {row.map((simbol, colIdx) => {
                  const isStopped = stoppedCols[colIdx]
                  const isWin = isOnWinPayline(rowIdx, colIdx)
                  const cellClass = `slot-cell${!isStopped ? ' cell-spinning' : ''}${isStopped && isWin ? ' win-cell' : ''}`
                  return (
                    <div key={colIdx} className={cellClass}>
                      <img src={`/symbols/${simbol}.png`} alt={simbol} className="slot-symbol-img" />
                    </div>
                  )
                })}
              </div>
            ))}
          </div>

          {mesaj && (
            <div className={`slot-mesaj slot-mesaj-${mesaj.tip}`}>
              {mesaj.tip === 'datorie' && <span className="mesaj-icon">⚠️</span>}
              {mesaj.tip === 'win' && <span className="mesaj-icon">⚠️</span>}
              {mesaj.tip === 'lose' && <span className="mesaj-icon">🔴</span>}
              {mesaj.text}
            </div>
          )}

          {showBaniAcasa && inDatarie && (
            <div className="datorie-options">
              {prieten && (
                <div className="datorie-card">
                  <div className="datorie-title">Suna pe {prieten.nume}...</div>
                  <div className="datorie-mesaj">"{prieten.mesaj}"</div>
                  <div className="datorie-suma">Poate imprumuta: {prieten.suma_disponibila} lei</div>
                  <button className="btn-primary" onClick={imprumutaDeLaPrieten}>
                    Imprumut de la {prieten.nume}
                  </button>
                </div>
              )}
              {baniDeAcasa >= (profile?.profil_financiar?.venit_lunar || 3000) ? (
                <div className="datorie-card bani-acasa-card epuizat">
                  <div className="datorie-title">💸 Venit lunar epuizat</div>
                  <div className="datorie-mesaj">Ai cheltuit tot venitul tau lunar de {profile?.profil_financiar?.venit_lunar || 3000} lei pe jocuri de noroc. Nu mai ai niciun ban de acasa.</div>
                </div>
              ) : (
                <div className="datorie-card bani-acasa-card">
                  <div className="datorie-title">💸 Bagi bani de acasa?</div>
                  <div className="datorie-mesaj">Bani pe care familia ta ii pierde. Mai ai disponibil: {Math.max(0, (profile?.profil_financiar?.venit_lunar || 3000) - baniDeAcasa).toFixed(0)} lei din {profile?.profil_financiar?.venit_lunar || 3000} lei</div>
                  <div className="bani-acasa-input">
                    <input
                      type="number"
                      min="1"
                      max={Math.max(0, (profile?.profil_financiar?.venit_lunar || 3000) - baniDeAcasa)}
                      value={sumaAcasa}
                      onChange={e => setSumaAcasa(e.target.value)}
                      placeholder="Suma in lei..."
                    />
                    <button className="btn-primary" onClick={bagaBaniDeAcasa}>
                      Baga bani
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SPIN TIER SELECTOR */}
          <div className="spin-controls">
            <div className="miza-selector">
              {MIZE.map(m => (
                <button
                  key={m}
                  className={`miza-btn ${mizaCurenta === m ? 'active' : ''}`}
                  onClick={() => setMizaCurenta(m)}
                  disabled={spinning}
                >{m} lei</button>
              ))}
            </div>
            <button
              className={`btn-spin ${spinning ? 'spinning' : ''}`}
              onClick={doSpin}
              disabled={spinning || autoplay}
            >
              {spinning ? '...' : audioPlaying ? '🔊' : `SPIN (${mizaCurenta} lei)`}
            </button>
            <button
              className={`btn-autoplay ${autoplay ? 'active' : ''}`}
              onClick={toggleAutoplay}
              disabled={inDatarie || sesiuneFinalizata}
            >
              {autoplay ? '⏹ STOP' : '▶ AUTO'}
            </button>
          </div>

          <div className="slot-info-row">
            <span>Miza: {mizaCurenta} lei/spin</span>
            <span>Spinuri ramase: ~{spinuriRamase}</span>
            <span>Spinuri jucate: {spinuri}</span>
          </div>
        </div>

        {/* BUGET IMPACT - sidebar */}
        <div className="slot-budgets">
          <h3>Impact cheltuieli</h3>
          <div className="budgets-grid">
            {Object.entries(cheltuieli || {}).filter(([, v]) => v > 0).map(([key, val]) => {
              const pierdut = impactCheltuieli[key]?.pierdut || 0
              const pct = Math.min(100, Math.round((pierdut / val) * 100))
              return (
                <div key={key} className="budget-impact-item">
                  <div className="bi-header">
                    <span className="bi-icon">{EXPENSE_ICONS[key] || '📦'}</span>
                    <span className="bi-label">{labelCheltuiala(key)}</span>
                  </div>
                  <div className="bi-bar">
                    <div className="bi-bar-fill" style={{ width: `${pct}%`, background: pct > 80 ? 'var(--red)' : pct > 40 ? 'var(--amber)' : 'var(--blue)' }} />
                  </div>
                  <div className="bi-vals">
                    <span>{val} lei</span>
                    {pierdut > 0 && <span className="red">-{pierdut} lei</span>}
                  </div>
                </div>
              )
            })}
          </div>
          {datoriiLista.length > 0 && (
            <div className="datorii-sidebar">
              <h4>Datorii ({datorieTotal} lei)</h4>
              <ul className="datorii-lista">
                {datoriiLista.map((d, i) => (
                  <li key={i}><span>{d.nume}</span><span className="red">{d.suma} lei</span></li>
                ))}
              </ul>
            </div>
          )}
          <button className="btn-ghost btn-stop" onClick={opreste}>
            Opreste sesiunea
          </button>
        </div>
      </div>
    </div>
  )
}

function labelCheltuiala(key) {
  const map = { mancare_zilnic: 'Mancare', utilitati_zilnic: 'Utilitati', rata_zilnic: 'Rata/Chirie', copii_zilnic: 'Copii', transport_zilnic: 'Transport', alte_zilnic: 'Altele' }
  return map[key] || key
}
